import { useCallback } from "react";
import { SubstituteSchedulerLesson } from "../components/scheduler/SubstituteScheduler";
import client from "../helpers/network/client";
import { Teacher } from "../models/teacher/teacher";
import {
  CreateVacancy,
  UpdateVacancyDto,
  Vacancy,
  VacancyDto,
  VacancyStatus,
} from "../models/vacancy/vacancy";
import { useTeachers } from "../states/cache-state/useTeachers";
import { useSchedulerApi } from "./scheduler-api";
import { useSchoolApi } from "./school-api";
import { CreateVacancyLesson, VacancyLesson } from "../models/schedule/lesson";
import { VacancyFilterParams } from "../models/filters";
import { removeDuplicatesSimple } from "../helpers/utils/array.util";
import { IsTruthy } from "../helpers/helper-functions";

const getVacancy = (id: string) =>
  client.get<VacancyDto>(`vacancy/${id}`).then((r) => r.data);
const getVacancies = ({
  status,
  sortBy,
  schoolId,
  absentTeacherEmail,
}: {
  status?: VacancyStatus;
  sortBy: keyof Vacancy;
  schoolId?: string;
  absentTeacherEmail?: string;
}) => {
  return client
    .get<VacancyDto[]>("vacancy", {
      params: {
        status,
        sortBy,
        schoolId,
        absentTeacherEmail,
      },
    })
    .then((r) => r.data);
};

const getVacanciesOfSchool = (schoolId: string) => {
  return client
    .get<VacancyDto[]>(`school/${schoolId}/vacancy`)
    .then((r) => r.data);
};

const createVacancy = (vacancy: CreateVacancy, schoolId: string) =>
  client
    .post<string>(`school/${schoolId}/vacancy`, vacancy)
    .then((r) => r.data);

const updateVacancy = (
  id: string,
  schoolId: string,
  vacancy: UpdateVacancyDto,
) =>
  client
    .put<void>(`school/${schoolId}/vacancy/${id}`, vacancy)
    .then((r) => r.data);

const acceptVacancyApplication = (
  schoolId: string,
  teacherId: string,
  vacancyId: string,
  lessons: string[],
) =>
  client.put(
    `school/${schoolId}/vacancy/${vacancyId}/teacher/${teacherId}/accept`,
    lessons,
  );

const declineVacancyApplication = (
  schoolId: string,
  teacherId: string,
  vacancyId: string,
  lessons: string[],
) =>
  client.put(
    `school/${schoolId}/vacancy/${vacancyId}/teacher/${teacherId}/decline`,
    lessons,
  );

const applyToVacancy = (
  schoolId: string,
  teacherId: string,
  vacancyId: string,
  lessons: string[],
) =>
  client.put(
    `school/${schoolId}/vacancy/${vacancyId}/teacher/${teacherId}`,
    lessons,
  );

const addVacancyLesson = (
  schoolId: string,
  vacancyId: string,
  lesson: CreateVacancyLesson,
) => client.post(`school/${schoolId}/vacancy/${vacancyId}/lesson`, lesson);

const removeVacancyLesson = (
  schoolId: string,
  vacancyId: string,
  lessonId: string,
) =>
  client.delete(`school/${schoolId}/vacancy/${vacancyId}/lesson/${lessonId}`);

const updateVacancyLessons = (
  schoolId: string,
  vacancyId: string,
  update: {
    lessonsToCreate?: CreateVacancyLesson[];
    lessonsToDelete?: string[];
  },
) =>
  client.put(`school/${schoolId}/vacancy/${vacancyId}/lesson`, {
    createLessons: update.lessonsToCreate ?? [],
    removeLessons: update.lessonsToDelete ?? [],
  });

const deleteVacancy = (id: string, schoolId: string) =>
  client.delete(`school/${schoolId}/vacancy/${id}`);

export const useVacancyApi = () => {
  const { getSchool } = useSchoolApi();
  const { getSchedule } = useSchedulerApi();
  const { getTeacher, getTeachersOfSchool } = useTeachers();

  const enrichVacancy = useCallback(
    async (vacancy: VacancyDto) => {
      const school = await getSchool(vacancy.schoolID);
      const schedule = await getSchedule(
        vacancy.schoolID,
        vacancy.absentTeacherEmail,
      );
      const teacher = await getTeacher(vacancy.absentTeacherEmail);

      // This logic needs rework if external teachers are introduced
      const teachers = await getTeachersOfSchool(vacancy.schoolID);

      return Vacancy.createFromVacancyDto(
        vacancy,
        teacher,
        teachers,
        schedule,
        school,
      );
    },
    [getSchedule, getSchool, getTeacher, getTeachersOfSchool],
  );

  const updateSubstitutesOnVacancyLessons = useCallback(
    async (
      vacancy: Vacancy,
      substitutes: Teacher[],
      lessons: SubstituteSchedulerLesson[],
      apply = true,
    ) => {
      const calls = substitutes.map((substitute) => {
        const substituteLessons = vacancy.lessons
          .filter((l) =>
            lessons
              // filter lessons that match with the current substitute
              .filter((sl) => sl.selectedSubstitute?.id === substitute.id)
              // Ensure that a matching lesson exists on the vacancy
              .some((sl) => VacancyLesson.equals(sl, l)),
          )
          // Do not accept / apply lessons where the same teacher already has applied / been accepted
          .filter((l) => l.teacherAccepted?.id !== substitute.id)
          .map((l) => l.id!);

        return substituteLessons.length
          ? (apply ? applyToVacancy : acceptVacancyApplication)(
              vacancy.school.id,
              substitute.id,
              vacancy.id,
              substituteLessons,
            )
          : Promise.resolve();
      });

      const filtered = vacancy.lessons.filter((vacancyLesson) => {
        const substituteLesson = lessons.find((l) =>
          VacancyLesson.equals(l, vacancyLesson),
        );
        return (
          (!substituteLesson && vacancyLesson.teacherAccepted) ||
          (substituteLesson &&
            vacancyLesson.teacherAccepted &&
            substituteLesson.selectedSubstitute?.id !==
              vacancyLesson.teacherAccepted?.id)
        );
      });
      const removeCalls = removeDuplicatesSimple(
        filtered.map((l) => l.teacherAccepted?.id).filter((t) => !!t),
      )
        .map((teacherId) => {
          const lessons = filtered.filter(
            (l) => l.teacherAccepted?.id === teacherId,
          );
          if (!teacherId || !lessons.length) {
            return undefined;
          }
          return declineVacancyApplication(
            vacancy.school.id,
            teacherId,
            vacancy.id,
            lessons.map((l) => l.id),
          );
        })
        .filter(IsTruthy);
      await Promise.allSettled([...calls, ...removeCalls]);
    },
    [],
  );

  const _getVacancy = useCallback(
    async (id: string) => {
      const vacancy = await getVacancy(id);
      return await enrichVacancy(vacancy);
    },
    [enrichVacancy],
  );

  const _updateVacancy = useCallback(
    async (id: string, schoolId: string, vacancy: UpdateVacancyDto) => {
      await updateVacancy(id, schoolId, { ...vacancy, lessons: [] });
      const updatedVacancy = await getVacancy(id);
      return await enrichVacancy(updatedVacancy);
    },
    [enrichVacancy],
  );

  const _updateVacancyWithLessons = useCallback(
    async (
      id: string,
      schoolId: string,
      oldLessons: VacancyLesson[],
      update: UpdateVacancyDto,
      lessonUpdate?: {
        substitutes: Teacher[];
        lessons: SubstituteSchedulerLesson[];
        mode: "apply" | "assign";
      },
    ) => {
      const lessonsToCreate = update.lessons.filter(
        (l) =>
          !oldLessons.some(
            (oldLesson) =>
              oldLesson.lessonIndex === l.vacancyLessonIndex &&
              oldLesson.lessonCode === l.vacancyLessonCode,
          ),
      );

      const lessonsToDelete = oldLessons
        .filter(
          (oldLesson) =>
            !update.lessons.some(
              (newLesson) =>
                oldLesson.lessonIndex === newLesson.vacancyLessonIndex &&
                oldLesson.lessonCode === newLesson.vacancyLessonCode,
            ),
        )
        .map((l) => l.id);

      let vacancy: Vacancy | undefined;

      await Promise.allSettled([
        updateVacancyLessons(schoolId, id, {
          lessonsToCreate,
          lessonsToDelete,
        }),
        _updateVacancy(id, schoolId, update).then((v) => (vacancy = v)),
      ]);

      if (vacancy) {
        await updateSubstitutesOnVacancyLessons(
          vacancy,
          lessonUpdate?.substitutes ?? [],
          lessonUpdate?.lessons ?? [],
          lessonUpdate?.mode === "apply",
        );
      }

      return await _getVacancy(id);
    },
    [_getVacancy, _updateVacancy, updateSubstitutesOnVacancyLessons],
  );

  const _getVacancies = useCallback(
    async (args: {
      status?: VacancyStatus;
      sortBy: keyof Vacancy;
      schoolId?: string;
      absentTeacherEmail?: string;
    }) => {
      const vacancies = await getVacancies(args);
      const enrichedVacancies = await Promise.all(
        vacancies.map(async (v) => await enrichVacancy(v)),
      );
      return enrichedVacancies;
    },
    [enrichVacancy],
  );

  const _getVacanciesOfSchool = useCallback(
    async (schoolId: string, filters?: Partial<VacancyFilterParams>) => {
      let vacancies = await getVacanciesOfSchool(schoolId);

      if (filters?.absentTeacherId) {
        vacancies = vacancies.filter(
          (v) => v.absentTeacherEmail === filters.absentTeacherId,
        );
      }

      const enrichedVacancies = await Promise.all(
        vacancies.map(async (v) => await enrichVacancy(v)),
      );
      return enrichedVacancies;
    },
    [enrichVacancy],
  );

  return {
    updateSubstitutesOnVacancyLessons,
    getVacancy: _getVacancy,
    createVacancy,
    acceptVacancyApplication,
    declineVacancyApplication,
    applyToVacancy,
    deleteVacancy,
    updateVacancyLessons,
    updateVacancy: _updateVacancy,
    getVacancies: _getVacancies,
    addVacancyLesson,
    removeVacancyLesson,
    getVacanciesOfSchool: _getVacanciesOfSchool,
    updateVacancyWithLessons: _updateVacancyWithLessons,
  };
};
